import { useMemo } from 'react'
import { useFamily } from './useFamily'
import type { Permission, FamilyRole } from '@/types'

interface PermissionState {
  // Role checks
  role: FamilyRole | null
  isParent: boolean
  isCaregiver: boolean
  isPrimaryCaregiver: boolean
  isViewer: boolean

  // Permission checks
  hasPermission: (permission: Permission) => boolean
  canViewHealth: boolean
  canManageHealth: boolean
  canViewFinancial: boolean
  canManageFinancial: boolean

  // Utility
  permissions: Permission[]
}

const CAREGIVER_ROLES: FamilyRole[] = ['PRIMARY_CAREGIVER', 'CAREGIVER']

export function usePermissions(): PermissionState {
  const { selectedFamily } = useFamily()

  return useMemo(() => {
    const role = selectedFamily?.role ?? null
    const isParent = role === 'PARENT'
    const isPrimaryCaregiver = role === 'PRIMARY_CAREGIVER'
    const isCaregiver = role !== null && CAREGIVER_ROLES.includes(role)
    const isViewer = role === 'VIEWER'

    const permissions = (Object.entries(selectedFamily?.permissions ?? {})
      .filter(([, allowed]) => allowed === true)
      .map(([permission]) => permission)) as Permission[]

    const hasPermission = (permission: Permission): boolean => {
      return permissions.includes(permission)
    }

    return {
      role,
      isParent,
      isCaregiver,
      isPrimaryCaregiver,
      isViewer,
      hasPermission,
      canViewHealth: hasPermission('view_health'),
      canManageHealth: hasPermission('manage_health'),
      canViewFinancial: hasPermission('view_financial'),
      canManageFinancial: hasPermission('manage_financial'),
      permissions,
    }
  }, [selectedFamily?.role, selectedFamily?.permissions])
}

// Helper to get role display name
export function getRoleDisplayName(role: FamilyRole): string {
  const names: Record<FamilyRole, string> = {
    PRIMARY_CAREGIVER: 'Primary Caregiver',
    CAREGIVER: 'Caregiver',
    PARENT: 'Parent',
    VIEWER: 'Viewer',
  }
  return names[role] ?? role
}

// Helper to get role description
export function getRoleDescription(role: FamilyRole): string {
  const descriptions: Record<FamilyRole, string> = {
    PRIMARY_CAREGIVER: 'Full access to manage family, health, and financial data',
    CAREGIVER: 'Configurable access to health and financial data',
    PARENT: 'View and manage your own data',
    VIEWER: 'Read-only access to family data',
  }
  return descriptions[role] ?? ''
}
